import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '../lib/utils.js';
import { isRtl } from '../lib/rtl.js';
import {
  SEGMENTED_SEGMENT_CLASS,
  SEGMENTED_SEGMENT_IDLE,
  SEGMENTED_SEGMENT_SELECTED,
  SEGMENTED_SEGMENT_TRACK_GEOMETRY,
  SEGMENTED_TRACK_CLASS,
} from './segmentedTrack.js';

/* ===========================================================================
 * Tabs — the strip PLUS its panels.
 *
 * The division of labour with `Segmented` (rules §5, one name per thing): a
 * strip with no panels of its own is a `Segmented` — a scope filter
 * (`role="radiogroup"`), or a view switcher over a region the page already
 * renders and names through `controls`. `Tabs` is for the case `Segmented`
 * cannot serve, where each trigger really does mount and unmount its own
 * `Tabs.Content` — today that is the worksite project page and nothing else.
 *
 * They draw ONE object. Every value of that object lives in
 * `./segmentedTrack.ts`; this file adds the panels, the registry and the
 * activation contract, and nothing about how a segment looks.
 * ======================================================================== */

type Orientation = 'horizontal' | 'vertical';

interface TabsContextValue {
  /** Currently selected tab value. */
  value: string;
  setValue: (next: string) => void;
  /** Shared base id used to derive deterministic tab + panel ids. */
  baseId: string;
  orientation: Orientation;
  /** Registered triggers (in DOM order) — used for arrow-key navigation. */
  register: (value: string, node: HTMLButtonElement | null) => void;
  /**
   * Move focus to the next/previous trigger (wrapping at the ends), or jump
   * straight to the first/last one — `'first'`/`'last'` are what Home/End
   * mean, which is not the same as "previous"/"next".
   */
  focusSibling: (current: string, move: 1 | -1 | 'first' | 'last') => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error(`<${component}> must be used inside <Tabs.Root>`);
  }
  return ctx;
}

interface RootProps {
  /** Controlled value. */
  value?: string;
  /** Initial value when uncontrolled. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /**
   * `manual` (the DEFAULT) — arrows move focus only; Enter or Space commits.
   * `automatic` — selection follows focus.
   *
   * The ARIA tabs pattern lets selection follow focus, and that is right only
   * when the panel is cheap. It was this component's unconditional behaviour
   * and it is wrong for the one screen that uses it: each of the worksite
   * project page's three tabs owns a DIFFERENT query and writes the URL, so
   * arrowing from Now to History fired the People fetch and a history replace
   * on the way past. `Segmented` has defaulted to `manual` for exactly that
   * reason since it was written; the two controls now agree, and a strip whose
   * panels are a re-render of data already in hand opts in to `automatic`.
   */
  activation?: 'manual' | 'automatic';
  orientation?: Orientation;
  className?: string;
  children: ReactNode;
}

function Root({
  value: controlledValue,
  defaultValue,
  onValueChange,
  activation = 'manual',
  orientation = 'horizontal',
  className,
  children,
}: RootProps): JSX.Element {
  const baseId = useId();
  const [uncontrolled, setUncontrolled] = useState<string>(defaultValue ?? '');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolled;

  const follows = activation === 'automatic';

  const triggersRef = useRef<Array<{ value: string; node: HTMLButtonElement }>>(
    []
  );

  const register = useCallback(
    (val: string, node: HTMLButtonElement | null) => {
      // Drop any prior registration for this value, then re-add if the node is
      // still mounted. This keeps the list in DOM order across re-renders.
      const next = triggersRef.current.filter((t) => t.value !== val);
      if (node) next.push({ value: val, node });
      triggersRef.current = next;
    },
    []
  );

  const setValue = useCallback(
    (next: string) => {
      if (!isControlled) setUncontrolled(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange]
  );

  const focusSibling = useCallback(
    (current: string, move: 1 | -1 | 'first' | 'last') => {
      // Sort registered triggers by their actual DOM order — `register` runs
      // during ref callbacks, which fire in mount order but can drift when
      // children re-order.
      const ordered = [...triggersRef.current].sort((a, b) => {
        if (a.node === b.node) return 0;

        return a.node.compareDocumentPosition(b.node) & Node.DOCUMENT_POSITION_FOLLOWING
          ? -1
          : 1;
      });
      const count = ordered.length;
      if (count === 0) return;
      // Home/End land on the first/last trigger outright — they are not
      // "previous"/"next" from wherever focus happens to be. They start from
      // OUTSIDE the strip and step inwards, so a disabled end cap is walked
      // past rather than landed on.
      const step = move === 'first' ? 1 : move === 'last' ? -1 : move;
      let cursor: number;
      if (move === 'first') cursor = -1;
      else if (move === 'last') cursor = count;
      else {
        cursor = ordered.findIndex((t) => t.value === current);
        if (cursor === -1) return;
      }

      // A DISABLED TRIGGER IS SKIPPED, not landed on. A `<button disabled>` is
      // not focusable, so `focus()` on one is a silent no-op — the walk used to
      // stop dead on it visually while still committing its value, which
      // selected a tab the reader could not have clicked. Stepping past keeps
      // every enabled choice reachable, which is what the key is for.
      for (let hops = 0; hops < count; hops += 1) {
        cursor = (cursor + step + count) % count;
        const target = ordered[cursor];
        if (!target || target.node.disabled) continue;
        target.node.focus();
        // `automatic` commits on arrival; `manual` leaves the value alone until
        // Enter or Space commits it through the trigger's own click.
        if (follows) setValue(target.value);
        return;
      }
    },
    [follows, setValue]
  );

  const ctx = useMemo<TabsContextValue>(
    () => ({ value, setValue, baseId, orientation, register, focusSibling }),
    [value, setValue, baseId, orientation, register, focusSibling]
  );

  return (
    <TabsContext.Provider value={ctx}>
      <div
        className={cn(
          orientation === 'vertical' ? 'flex gap-4' : undefined,
          className
        )}
        data-orientation={orientation}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
}

const List = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useTabsContext('Tabs.List');
    return (
      <div
        ref={ref}
        role="tablist"
        aria-orientation={orientation}
        className={cn(
          // THE SHARED TRACK. This used to be a private spelling — `bg-muted`,
          // `p-1`, `gap-1`, no floor and, load-bearing, NO WRAP — which is the
          // page-pushes-sideways defect `segmentedTrack.ts` documents at
          // length. `Segmented` had been fixed; this file had not, because the
          // fix lived in the other one.
          SEGMENTED_TRACK_CLASS,
          orientation === 'vertical' && 'flex-col',
          className
        )}
        {...props}
      />
    );
  }
);
List.displayName = 'Tabs.List';

interface TriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ value, className, onKeyDown, onClick, ...props }, ref) => {
    const ctx = useTabsContext('Tabs.Trigger');
    const innerRef = useRef<HTMLButtonElement | null>(null);
    const selected = ctx.value === value;

    // Combine the consumer's ref with our internal ref so the parent can still
    // attach to the trigger node while we keep an internal handle for the
    // registry/focus logic.
    const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
        innerRef.current = node;
        ctx.register(value, node);
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      },
      [ctx, ref, value]
    );

    useEffect(() => {
      // Clean up the registry entry when the trigger unmounts.
      return () => ctx.register(value, null);
    }, [ctx, value]);

    function handleKey(e: KeyboardEvent<HTMLButtonElement>): void {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      const isHorizontal = ctx.orientation === 'horizontal';
      // The key points at a direction ON SCREEN, not at an array index — in
      // an RTL page ArrowRight moves towards the strip's START. Vertical
      // orientation has no mirrored axis, so it stays direction-blind.
      const rtl = isHorizontal && isRtl(e.currentTarget);
      const nextKey = isHorizontal ? (rtl ? 'ArrowLeft' : 'ArrowRight') : 'ArrowDown';
      const prevKey = isHorizontal ? (rtl ? 'ArrowRight' : 'ArrowLeft') : 'ArrowUp';
      if (e.key === nextKey) {
        e.preventDefault();
        ctx.focusSibling(value, 1);
      } else if (e.key === prevKey) {
        e.preventDefault();
        ctx.focusSibling(value, -1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        ctx.focusSibling(value, 'first');
      } else if (e.key === 'End') {
        e.preventDefault();
        ctx.focusSibling(value, 'last');
      }
    }

    return (
      <button
        type="button"
        ref={setRefs}
        role="tab"
        id={`${ctx.baseId}-trigger-${value}`}
        aria-selected={selected}
        // ONLY THE SELECTED TRIGGER POINTS AT A PANEL. `Tabs.Content` renders
        // `null` while inactive unless `forceMount`, so `aria-controls` on the
        // other triggers named ids that were not in the document — a control
        // claiming a relationship to nothing, which is worse than claiming
        // none. The selected trigger's panel is always mounted, so its id
        // always resolves.
        aria-controls={selected ? `${ctx.baseId}-content-${value}` : undefined}
        // Roving tabindex — only the active tab is reachable via Tab from
        // outside the tablist.
        tabIndex={selected ? 0 : -1}
        onClick={(e) => {
          onClick?.(e);
          ctx.setValue(value);
        }}
        onKeyDown={handleKey}
        className={cn(
          // ONE SEGMENT, drawn from `segmentedTrack.ts` like `Segmented`'s.
          // What this replaced was the same control four steps out: the 2px
          // HAIRLINE radius role on a pill inside a 10px track, `text-label`
          // where a chosen word sits at the body step, an OUTSET focus ring
          // drawn over the track's shoulder, and a `transition-colors` that
          // kept animating a disabled trigger (rules §1 forbids that outright).
          SEGMENTED_SEGMENT_CLASS,
          SEGMENTED_SEGMENT_TRACK_GEOMETRY,
          selected ? SEGMENTED_SEGMENT_SELECTED : SEGMENTED_SEGMENT_IDLE,
          className
        )}
        {...props}
      />
    );
  }
);
Trigger.displayName = 'Tabs.Trigger';

interface ContentProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
  /** Keep the panel mounted while inactive (useful for forms that should
   *  preserve state). Default `false` — inactive panels are unmounted. */
  forceMount?: boolean;
}

const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ value, forceMount, hidden, className, ...props }, ref) => {
    const ctx = useTabsContext('Tabs.Content');
    const selected = ctx.value === value;
    if (!selected && !forceMount) return null;
    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`${ctx.baseId}-content-${value}`}
        aria-labelledby={`${ctx.baseId}-trigger-${value}`}
        hidden={hidden ?? !selected}
        tabIndex={0}
        className={cn('mt-element focus-visible:outline-none', className)}
        {...props}
      />
    );
  }
);
Content.displayName = 'Tabs.Content';

export const Tabs = { Root, List, Trigger, Content };
